import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useApiClient } from "@/lib/auth";

interface VerifyEmailSearch {
  token?: string;
  type?: string;
}

export const Route = createFileRoute("/auth/verify-email")({
  validateSearch: (search: Record<string, unknown>): VerifyEmailSearch => ({
    token:
      typeof search.token === "string" && /^[a-f0-9]{64}$/.test(search.token)
        ? search.token
        : undefined,
    type: typeof search.type === "string" ? search.type : undefined,
  }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { t } = useTranslation(["auth", "common"]);
  const api = useApiClient();
  const { token, type } = Route.useSearch();

  const verifyMutation = useMutation({
    mutationFn: async (verifyToken: string) => {
      const result =
        type === "account"
          ? await api.POST("/auth/verify-account-email", {
              body: { token: verifyToken },
            })
          : await api.POST("/auth/verify-email", {
              body: { token: verifyToken },
            });
      if (result.error) {
        if (result.response.status === 401) {
          throw new Error("invalid_token");
        }
        throw result.error;
      }
    },
  });

  // Attempt deep link to mobile app, then auto-verify
  useEffect(() => {
    if (token) {
      const deepLink = `njbsoft-field://verify-email?token=${encodeURIComponent(token)}`;
      window.location.href = deepLink;

      // After a brief delay, proceed with web verification
      const timeout = setTimeout(() => {
        verifyMutation.mutate(token);
      }, 500);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const isInvalidToken =
    verifyMutation.isError &&
    verifyMutation.error instanceof Error &&
    verifyMutation.error.message === "invalid_token";

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <div>
          <h2 className="text-center text-3xl font-bold text-on-surface">
            {t("verifyEmailTitle")}
          </h2>
        </div>

        {verifyMutation.isPending && (
          <div className="text-center text-sm text-muted">{t("verifyingEmail")}</div>
        )}

        {verifyMutation.isSuccess && (
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
              {t("emailVerified")}
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </div>
        )}

        {isInvalidToken && (
          <div className="space-y-4">
            <div className="rounded-md bg-danger/10 p-3 text-sm text-danger">
              {t("invalidVerifyToken")}
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </div>
        )}

        {verifyMutation.isError && !isInvalidToken && (
          <div className="space-y-4">
            <div className="rounded-md bg-danger/10 p-3 text-sm text-danger">
              {t("errors.somethingWentWrong")}
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </div>
        )}

        {!token && !verifyMutation.isPending && (
          <div className="space-y-4">
            <div className="rounded-md bg-danger/10 p-3 text-sm text-danger">
              {t("invalidVerifyToken")}
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
